import { useState, useEffect } from "react"
import { auth, db } from "../firebase"
import { doc, getDoc, updateDoc } from "firebase/firestore"
import { updateProfile } from "firebase/auth"
import { useNavigate } from "react-router-dom"
import {
  LuUser,
  LuGraduationCap,
  LuCalendar,
  LuBuilding2,
  LuTarget,
  LuTrendingUp,
  LuClock,
  LuSparkles
} from "react-icons/lu"
import "./AboutYou.css"

function AboutYou() {
  const [name, setName] = useState("")
  const [branch, setBranch] = useState("")
  const [year, setYear] = useState("")
  const [college, setCollege] = useState("")
  const [careerGoal, setCareerGoal] = useState("")
  const [skillLevel, setSkillLevel] = useState("")
  const [weeklyCommitment, setWeeklyCommitment] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [phase, setPhase] = useState("form")
  const [imageLoaded, setImageLoaded] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchUser = async () => {
      const user = auth.currentUser
      if (!user) {
        navigate("/login", { replace: true })
        return
      }

      const snap = await getDoc(doc(db, "users", user.uid))
      if (snap.exists()) {
        const data = snap.data()
        if (data.informationProvided === true) {
          navigate("/dashboard", { replace: true })
          return
        }
        setName(data.name || "")
        setBranch(data.branch || "")
        setYear(data.year || "")
        setCollege(data.college || "")
        setCareerGoal(data.careerGoal || "")
        setSkillLevel(data.skillLevel || "")
        setWeeklyCommitment(data.weeklyCommitment || "")
      }
      setLoading(false)
    }
    fetchUser()
  }, [navigate])

  useEffect(() => {
    if (careerGoal === "Not Sure Yet") {
      setSkillLevel("Beginner")
    }
  }, [careerGoal])

  const handleSubmit = async () => {
    const finalSkillLevel =
      careerGoal === "Not Sure Yet" ? "Beginner" : skillLevel

    if (
      !name ||
      !branch ||
      !year ||
      !college ||
      !careerGoal ||
      !finalSkillLevel ||
      !weeklyCommitment
    ) return

    setSaving(true)
    const user = auth.currentUser

    await new Promise(resolve => setTimeout(resolve, 2300))

    await updateDoc(doc(db, "users", user.uid), {
      name,
      branch,
      year,
      college,
      careerGoal,
      skillLevel: finalSkillLevel,
      weeklyCommitment,
      informationProvided: true
    })

    await updateProfile(user, { displayName: name })

    setPhase("building1")

    setTimeout(() => setPhase("building2"), 2000)
    setTimeout(() => navigate("/dashboard", { replace: true }), 4000)
  }

  if (loading) return null

  if (phase !== "form") {
    return (
      <div className="building-container">
        <div className="building-header">
          <div className="sparkle-ring">
            <LuSparkles className="building-icon" />
          </div>
          <div className="progress-dots">
            <div className={`dot ${phase === "building1" ? "active" : ""}`}></div>
            <div className={`dot ${phase === "building2" ? "active" : ""}`}></div>
          </div>
        </div>

        <div className="building-content">
          {phase === "building1" && (
            <div className="phase-content">
              <h2 className="animated-text">
                <span className="gradient-text">Building</span> your personalized feed
              </h2>
              <p className="phase-subtitle">Analyzing your preferences and goals</p>
              <div className="feature-icons">
                <LuTarget className="feature-icon" />
                <LuTrendingUp className="feature-icon" />
                <LuGraduationCap className="feature-icon" />
              </div>
            </div>
          )}
          {phase === "building2" && (
            <div className="phase-content">
              <h2 className="animated-text">
                <span className="gradient-text">Optimizing</span> your learning roadmap
              </h2>
              <p className="phase-subtitle">Creating the perfect path for your success</p>
              <div className="feature-icons">
                <LuClock className="feature-icon" />
                <LuBuilding2 className="feature-icon" />
                <LuUser className="feature-icon" />
              </div>
            </div>
          )}
        </div>

        <div className="building-footer">
          <div className="loader-container">
            <div className="loader large"></div>
            <div className="loader-pulse"></div>
          </div>
          <p className="building-tip">
            {phase === "building1" ? "This will only take a moment..." : "Almost ready!"}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="about-container">
      <div className="about-box entry-animate">

        {!imageLoaded && <div className="about-image-skeleton"></div>}

        <img
          src="/assets/about.png"
          alt="About You"
          className="about-logo"
          onLoad={() => setImageLoaded(true)}
          style={{ display: imageLoaded ? "block" : "none" }}
        />

        <h2 className="title-animate">
          Tell us about you{name ? `, ${name.split(" ")[0]}` : ""}
        </h2>

        <p className="about-subtitle fade-in-delay">
          This helps us personalize your roadmap.
        </p>

        <div className="input-wrapper">
          <LuUser className="input-icon blue" />
          <input type="text" placeholder="Your Full Name" value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div className="input-wrapper">
          <LuGraduationCap className="input-icon purple" />
          <select value={branch} onChange={e => setBranch(e.target.value)}>
            <option value="">Select Branch</option>
            <option>Computer Science & Engineering</option>
            <option>Information Technology</option>
          </select>
        </div>

        <div className="input-wrapper">
          <LuCalendar className="input-icon orange" />
          <select value={year} onChange={e => setYear(e.target.value)}>
            <option value="">Select Year</option>
            <option>First Year</option>
            <option>Second Year</option>
            <option>Third Year</option>
            <option>Final Year</option>
          </select>
        </div>

        <div className="input-wrapper">
          <LuBuilding2 className="input-icon teal" />
          <input type="text" placeholder="College Name" value={college} onChange={e => setCollege(e.target.value)} />
        </div>

        <div className="input-wrapper">
          <LuTarget className="input-icon pink" />
          <select value={careerGoal} onChange={e => setCareerGoal(e.target.value)}>
            <option value="">Career Goal</option>
            <option>Software Developer</option>
            <option>AI / ML Engineer</option>
            <option>Not Sure Yet</option>
          </select>
        </div>

        {careerGoal !== "Not Sure Yet" && (
          <div className="input-wrapper">
            <LuTrendingUp className="input-icon green" />
            <select value={skillLevel} onChange={e => setSkillLevel(e.target.value)}>
              <option value="">Skill Level</option>
              <option>Beginner</option>
              <option>Intermediate</option>
              <option>Advanced</option>
            </select>
          </div>
        )}

        <div className="input-wrapper">
          <LuClock className="input-icon red" />
          <select value={weeklyCommitment} onChange={e => setWeeklyCommitment(e.target.value)}>
            <option value="">Weekly Commitment</option>
            <option>Less than 5 hrs</option>
            <option>5–10 hrs</option>
            <option>10–20 hrs</option>
            <option>20+ hrs</option>
          </select>
        </div>

        <button className="about-btn" onClick={handleSubmit} disabled={saving}>
          {saving ? <div className="loader"></div> : "Continue"}
        </button>

      </div>
    </div>
  )
}

export default AboutYou